// ── Leitura do desfecho da cobrança ───────────────────────────────
// 🔴 O endpoint de cobrança responde 200 em quase tudo que NÃO cobrou. Os dois
// desfechos que mais custam dinheiro — `skipped_below_min` e `skipped_no_address`
// — não são erro: chegam como 200 com `cobranca_emitida: false`. Quem inferir
// "cobrou" do HTTP fecha a venda achando que faturou.

import type { BluehubCobranca, BluehubMarca, BluehubSituacaoCobranca } from '../ports/types';

/** O que fazer diante do desfecho. É isto que a tela deve refletir. */
export type AcaoPosCobranca =
  | 'nada'                    // cobrou, ou não há o que fazer por aqui
  | 'provisionar_ate_o_piso'
  | 'corrigir_endereco'
  | 'trocar_cartao'
  | 'provisionar_vidas'
  | 'esperar_ciclo'
  | 'falar_com_a_ed';

export interface DesfechoCobranca {
  cobrou: boolean;
  situacao: BluehubSituacaoCobranca;
  acao: AcaoPosCobranca;
  /** `true` quando repetir a chamada dá exatamente o mesmo resultado. */
  repetirNaoAdianta: boolean;
  /** Repetir só depois de tratar a causa, e com Idempotency-Key NOVA. */
  exigeChaveNova: boolean;
  /** O e-mail do boleto saiu do lado do Hub? Se não, a entrega é nossa. */
  emailDoBoletoSaiu: boolean;
  mensagem: string;
}

const PLANO: Record<BluehubSituacaoCobranca, { acao: AcaoPosCobranca; chaveNova: boolean; mensagem: string }> = {
  charged: { acao: 'nada', chaveNova: false, mensagem: 'Cobrança emitida.' },

  card_declined: { acao: 'trocar_cartao', chaveNova: true, mensagem: 'O emissor recusou o cartão.' },
  card_unavailable: { acao: 'trocar_cartao', chaveNova: true, mensagem: 'Não há cartão utilizável na conta.' },

  skipped_below_min: {
    acao: 'provisionar_ate_o_piso', chaveNova: true,
    mensagem: 'A empresa está abaixo do piso de vidas faturáveis da marca — nada foi cobrado.',
  },
  skipped_no_address: {
    acao: 'corrigir_endereco', chaveNova: true,
    mensagem: 'Falta o endereço de cobrança (cidade/UF/CEP) — nada foi cobrado.',
  },
  skipped_zero: {
    acao: 'provisionar_vidas', chaveNova: true,
    mensagem: 'Não há vaga faturável na conta — nada foi cobrado.',
  },
  skipped_idempotent: {
    acao: 'nada', chaveNova: false,
    mensagem: 'Esta competência já foi cobrada. Cobrar de novo seria cobrança dupla.',
  },
  skipped_mesmo_mes_vencimento: {
    acao: 'esperar_ciclo', chaveNova: false,
    mensagem: 'Já existe título com vencimento no mesmo mês; antecipar criaria duas faturas.',
  },
  skipped_exempt: { acao: 'nada', chaveNova: false, mensagem: 'A conta está isenta neste período.' },
  skipped_internal_account: { acao: 'nada', chaveNova: false, mensagem: 'É conta interna da ED e não se cobra.' },
  skipped_flag_off: { acao: 'falar_com_a_ed', chaveNova: false, mensagem: 'O motor de cobrança está desligado.' },
  skipped_legacy_engine: { acao: 'falar_com_a_ed', chaveNova: false, mensagem: 'A conta está no modelo antigo de faturamento.' },
};

/** O e-mail mensal do boleto exige TRÊS condições juntas. Quando o motor adota
 *  um título que já existia, nenhum e-mail sai — e aí a entrega é nossa, com o
 *  `boleto_url` e a linha digitável que a resposta trouxe. */
export const emailDoBoletoSaiu = (cobranca: BluehubCobranca): boolean =>
  cobranca.situacao === 'charged' &&
  cobranca.forma_pagamento === 'boleto' &&
  String(cobranca.detalhe || '').startsWith('enterprise_day12_');

export function lerDesfechoCobranca(cobranca: BluehubCobranca): DesfechoCobranca {
  const plano = PLANO[cobranca.situacao] ?? {
    acao: 'falar_com_a_ed' as const,
    chaveNova: false,
    mensagem: `Desfecho não previsto: ${cobranca.situacao}.`,
  };
  return {
    cobrou: cobranca.cobranca_emitida === true,
    situacao: cobranca.situacao,
    acao: plano.acao,
    repetirNaoAdianta: plano.acao === 'nada' || plano.acao === 'esperar_ciclo' || plano.acao === 'falar_com_a_ed',
    exigeChaveNova: plano.chaveNova,
    emailDoBoletoSaiu: emailDoBoletoSaiu(cobranca),
    mensagem: plano.mensagem,
  };
}

/** Piso de vidas faturáveis, por marca da CONTA. No BlueHub são 3 por empresa,
 *  sem isenção: venda fechada com 1 ou 2 vidas NUNCA é cobrada, e a chamada
 *  responde 200. Isto é validação de fechamento, não descoberta no dia 12. */
export const PISO_VIDAS_FATURAVEIS: Record<BluehubMarca, number> = {
  bluehub: 3,
  // Contas edp/tfo criadas por esta API têm a matriz como vendedora e não têm piso.
  edp: 0,
  tfo: 0,
};

export const atingiuOPiso = (marca: BluehubMarca, vidasFaturaveis: number): boolean =>
  vidasFaturaveis >= (PISO_VIDAS_FATURAVEIS[marca] ?? 0);
